import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Avatar,
  LinearProgress,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  Pagination
} from '@mui/material';
import {
  People,
  CheckCircle,
  Cancel,
  Visibility,
  Edit,
  MoreVert,
  Refresh,
  Search,
  FilterList,
  AccountBalance,
  Business,
  TrendingUp,
  Add,
  Lock,
  LockOpen
} from '@mui/icons-material';
import {
  getMerchants,
  getMerchantById,
  updateMerchant,
  approveRejectKyc,
  getPendingKyc,
  getMerchantKycDocs,
  createMerchant,
  setMerchantLien,
  releaseMerchantLien
} from '../../services/adminApi';
import { showLoadingToast, updateToast, showSuccessToast, showErrorToast } from '../../utils/toast';
import { formatDateDDMMYYYY } from '../../utils/dateUtils';

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`merchant-tabpanel-${index}`}
      aria-labelledby={`merchant-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const MerchantManagement = () => {
  const [tabValue, setTabValue] = useState(0);
  const [merchants, setMerchants] = useState([]);
  const [pendingKyc, setPendingKyc] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [kycDialogOpen, setKycDialogOpen] = useState(false);
  const [selectedKycDocs, setSelectedKycDocs] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [lienDialogOpen, setLienDialogOpen] = useState(false);
  const [lienAction, setLienAction] = useState('add');
  const [lienData, setLienData] = useState({ amount: '', reason: '' });
  const [newMerchant, setNewMerchant] = useState({
    merchantName: '',
    businessName: '',
    email: '',
    phone: '',
    rateLimitRequests: 100,
    rateLimitWindow: 60,
    initialBalance: 0
  });

  useEffect(() => {
    loadMerchants();
    loadPendingKyc();
  }, [page, statusFilter, searchTerm]);

  const loadMerchants = async () => {
    try {
      setLoading(true);
      const response = await getMerchants(page, 20, statusFilter, searchTerm);

      // Handle different response structures
      if (response.data) {
        if (Array.isArray(response.data)) {
          // Direct array response
          setMerchants(response.data);
          setTotalPages(1); // Assume single page if no pagination info
        } else if (response.data.content && Array.isArray(response.data.content)) {
          // Paginated response with content array
          setMerchants(response.data.content);
          setTotalPages(response.data.totalPages || response.data.totalPages || 1);
        } else {
          // Fallback
          setMerchants([]);
          setTotalPages(1);
        }
      } else {
        setMerchants([]);
        setTotalPages(1);
      }

      setError('');
    } catch (error) {
      console.error('Failed to load merchants:', error);

      // Enhanced error handling
      let errorMessage = 'Failed to load merchants';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      setMerchants([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingKyc = async () => {
    try {
      const response = await getPendingKyc(0, 10);
      setPendingKyc(response.data?.content || response.data || []);
    } catch (error) {
      console.error('Failed to load pending KYC:', error);
      setPendingKyc([]);
    }
  };

  const handleMenuClick = (event, merchant) => {
    setAnchorEl(event.currentTarget);
    setSelectedMerchant(merchant);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMerchant(null);
  };

  const handleViewMerchant = async (merchant) => {
    try {
      const response = await getMerchantById(merchant.merchantId || merchant.id);
      setSelectedMerchant(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      showErrorToast('Failed to load merchant details');
    }
  };

  const handleViewKyc = async (merchant) => {
    try {
      const response = await getMerchantKycDocs(merchant.merchantId || merchant.id);
      setSelectedKycDocs(response.data);
      setSelectedMerchant(merchant);
      setKycDialogOpen(true);
    } catch (error) {
      showErrorToast('Failed to load KYC documents');
    }
  };

  const handleApproveRejectKyc = async (merchant, action) => {
    const toastId = showLoadingToast(`${action}ing KYC...`);

    try {
      await approveRejectKyc(merchant.merchantId || merchant.id, {
        approved: action === 'approve',
        rejectionReason: action === 'reject' ? 'Rejected by admin' : null
      });

      updateToast(toastId, `KYC ${action}d successfully!`, 'success');
      loadPendingKyc();
      loadMerchants();
    } catch (error) {
      updateToast(toastId, `Failed to ${action} KYC`, 'error');
    }
  };

  const handleUpdateStatus = async (merchant, newStatus) => {
    const toastId = showLoadingToast('Updating merchant status...');

    try {
      await updateMerchant(merchant.merchantId || merchant.id, { status: newStatus });
      updateToast(toastId, 'Status updated successfully!', 'success');
      loadMerchants();
    } catch (error) {
      updateToast(toastId, 'Failed to update status', 'error');
    }
  };

  const handleAddMerchant = async () => {
    // Validation
    if (!newMerchant.merchantName || !newMerchant.businessName || !newMerchant.email || !newMerchant.phone) {
      showErrorToast('Please fill all mandatory fields');
      return;
    }

    const payload = {
      ...newMerchant,
      rateLimitRequests: Number(newMerchant.rateLimitRequests),
      rateLimitWindow: Number(newMerchant.rateLimitWindow),
      initialBalance: Number(newMerchant.initialBalance)
    };

    const toastId = showLoadingToast('Creating merchant...');
    try {
      await createMerchant(payload);
      updateToast(toastId, 'Merchant created successfully!', 'success');
      setAddDialogOpen(false);
      setNewMerchant({
        merchantName: '',
        businessName: '',
        email: '',
        phone: '',
        rateLimitRequests: 100,
        rateLimitWindow: 60,
        initialBalance: 0
      });
      loadMerchants();
    } catch (error) {
      console.error('Failed to create merchant:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create merchant';
      updateToast(toastId, errorMessage, 'error');
    }
  };

  const handleNewMerchantChange = (e) => {
    const { name, value } = e.target;
    setNewMerchant(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOpenLienDialog = (merchant, action) => {
    setSelectedMerchant(merchant);
    setLienAction(action);
    setLienData({ 
      amount: action === 'add' ? (merchant.lienAmount || '') : '', 
      reason: '' 
    });
    setLienDialogOpen(true);
  };

  const handleLienSubmit = async () => {
    if (lienAction === 'add') {
      if (!lienData.amount || isNaN(lienData.amount) || Number(lienData.amount) <= 0) {
        showErrorToast('Please enter a valid amount');
        return;
      }
      if (!lienData.reason) {
        showErrorToast('Please enter a reason');
        return;
      }
    }

    const toastId = showLoadingToast(`${lienAction === 'add' ? 'Updating' : 'Releasing'} lien...`);
    
    try {
      if (lienAction === 'add') {
        await setMerchantLien(selectedMerchant.merchantId || selectedMerchant.id, {
          lienAmount: Number(lienData.amount),
          reason: lienData.reason
        });
      } else {
        await releaseMerchantLien(selectedMerchant.merchantId || selectedMerchant.id);
      }
      
      updateToast(toastId, `Lien ${lienAction === 'add' ? 'updated' : 'released'} successfully!`, 'success');
      setLienDialogOpen(false);
      loadMerchants();
    } catch (error) {
      console.error(`Failed to ${lienAction} lien:`, error);
      const errorMessage = error.response?.data?.message || error.message || `Failed to ${lienAction} lien`;
      updateToast(toastId, errorMessage, 'error');
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'success';
      case 'pending': return 'warning';
      case 'suspended': return 'error';
      case 'inactive': return 'default';
      default: return 'default';
    }
  };

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ bgcolor: `${color}.main`, mr: 2 }}>
            <Icon />
          </Avatar>
          <Box>
            <Typography variant="h4" component="div">
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
        Merchant Management
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Merchants"
            value={merchants.length}
            icon={People}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending KYC"
            value={pendingKyc.length}
            icon={Business}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Merchants"
            value={merchants.filter(m => m.status === 'ACTIVE').length}
            icon={CheckCircle}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Suspended"
            value={merchants.filter(m => m.status === 'SUSPENDED').length}
            icon={Cancel}
            color="error"
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="All Merchants" />
          <Tab label={`Pending KYC (${pendingKyc.length})`} />
        </Tabs>
      </Box>

      {/* All Merchants Tab */}
      <TabPanel value={tabValue} index={0}>
        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search merchants..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1 }} />
            }}
            sx={{ minWidth: 250 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="SUSPENDED">Suspended</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setAddDialogOpen(true)}
            sx={{ boxShadow: 2 }}
          >
            Add Merchant
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadMerchants}
          >
            Refresh
          </Button>
        </Box>

        {/* Merchants Table */}
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow>
                <TableCell>Merchant</TableCell>
                <TableCell>Business Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Wallet Balance</TableCell>
                <TableCell>Lien Amount</TableCell>
                <TableCell>Usable Balance</TableCell>
                <TableCell>Lien Action</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>KYC Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : merchants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" sx={{ py: 3 }}>
                      No merchants found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                merchants.map((merchant) => (
                  <TableRow key={merchant.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                          {merchant.merchantName?.charAt(0) || merchant.name?.charAt(0) || 'M'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {merchant.merchantName || merchant.name || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {merchant.merchantId || merchant.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{merchant.businessName || 'N/A'}</TableCell>
                    <TableCell>{merchant.email || 'N/A'}</TableCell>
                    <TableCell>{merchant.phone || 'N/A'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        ₹{merchant.walletBalance?.toFixed(2) || '0.00'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium" color={merchant.lienAmount > 0 ? "error" : "text.secondary"}>
                        ₹{merchant.lienAmount?.toFixed(2) || '0.00'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium" color="success.main">
                        ₹{merchant.usableBalance?.toFixed(2) || '0.00'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenLienDialog(merchant, 'add')}
                        title="Set Lien Amount"
                      >
                        <Lock />
                      </IconButton>
                      {merchant.lienAmount > 0 && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenLienDialog(merchant, 'release')}
                          title="Release Lien"
                        >
                          <LockOpen />
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={merchant.status || 'Unknown'}
                        color={getStatusColor(merchant.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={merchant.kycStatus || 'Not Submitted'}
                        color={merchant.kycStatus === 'VERIFIED' ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => handleMenuClick(e, merchant)}
                        size="small"
                      >
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, px: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {(page - 1) * 20 + 1}-{Math.min(page * 20, merchants.length)} of {merchants.length}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outline"
                size="small"
                onClick={() => setPage(page - 1)}
                disabled={page === 1 || loading}
              >
                Previous
              </Button>
              {Array.from({ length: totalPages }, (_, i) => (
                <Button
                  key={i + 1}
                  variant={page === i + 1 ? 'contained' : 'outline'}
                  size="small"
                  onClick={() => setPage(i + 1)}
                  disabled={loading}
                >
                  {i + 1}
                </Button>
              ))}
              <Button
                variant="outline"
                size="small"
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages || loading}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}
      </TabPanel>

      {/* Pending KYC Tab */}
      <TabPanel value={tabValue} index={1}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Merchant</TableCell>
                <TableCell>Business Name</TableCell>
                <TableCell>Submitted Date</TableCell>
                <TableCell>Documents</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : pendingKyc.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" sx={{ py: 3 }}>
                      No pending KYC requests
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pendingKyc.map((kyc) => (
                  <TableRow key={kyc.merchantId || kyc.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2, bgcolor: 'warning.main' }}>
                          {kyc.merchantName?.charAt(0) || 'M'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {kyc.merchantName || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {kyc.merchantId || kyc.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{kyc.businessName || 'N/A'}</TableCell>
                    <TableCell>
                      {kyc.submittedAt ? formatDateDDMMYYYY(kyc.submittedAt) : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        startIcon={<Visibility />}
                        onClick={() => handleViewKyc(kyc)}
                      >
                        View Documents
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircle />}
                        onClick={() => handleApproveRejectKyc(kyc, 'approve')}
                        sx={{ mr: 1 }}
                      >
                        Approve
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<Cancel />}
                        onClick={() => handleApproveRejectKyc(kyc, 'reject')}
                      >
                        Reject
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          const merchant = selectedMerchant;
          handleMenuClose();
          handleViewMerchant(merchant);
        }}>
          <Visibility sx={{ mr: 1 }} /> View Details
        </MenuItem>
        <MenuItem onClick={() => {
          const merchant = selectedMerchant;
          handleMenuClose();
          handleViewKyc(merchant);
        }}>
          <Business sx={{ mr: 1 }} /> View KYC
        </MenuItem>
        {selectedMerchant?.status !== 'ACTIVE' && (
          <MenuItem onClick={() => {
            const merchant = selectedMerchant;
            handleMenuClose();
            handleUpdateStatus(merchant, 'ACTIVE');
          }}>
            <CheckCircle sx={{ mr: 1 }} /> Activate
          </MenuItem>
        )}
        {selectedMerchant?.status !== 'SUSPENDED' && (
          <MenuItem onClick={() => {
            const merchant = selectedMerchant;
            handleMenuClose();
            handleUpdateStatus(merchant, 'SUSPENDED');
          }}>
            <Cancel sx={{ mr: 1 }} /> Suspend
          </MenuItem>
        )}
      </Menu>

      {/* Merchant Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Merchant Details</DialogTitle>
        <DialogContent>
          {selectedMerchant && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Merchant ID
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedMerchant.merchantId || selectedMerchant.id}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedMerchant.merchantName || selectedMerchant.name || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Business Name
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedMerchant.businessName || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedMerchant.email || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Phone
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedMerchant.phone || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Wallet Balance
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  ₹{selectedMerchant.walletBalance?.toFixed(2) || '0.00'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Usable Balance
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  ₹{selectedMerchant.usableBalance?.toFixed(2) || '0.00'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Rate Limit
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedMerchant.rateLimitRequests || 0} req/{selectedMerchant.rateLimitWindow || 60}s
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={selectedMerchant.status || 'Unknown'}
                  color={getStatusColor(selectedMerchant.status)}
                  sx={{ mb: 2 }}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* KYC Documents Dialog */}
      <Dialog
        open={kycDialogOpen}
        onClose={() => setKycDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>KYC Documents</DialogTitle>
        <DialogContent>
          {selectedKycDocs && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {selectedMerchant?.merchantName || 'Merchant'} KYC Documents
              </Typography>
              {selectedKycDocs.documents && selectedKycDocs.documents.length > 0 ? (
                selectedKycDocs.documents.map((doc, index) => (
                  <Card key={index} sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        {doc.documentType || `Document ${index + 1}`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Status: {doc.status || 'Pending'}
                      </Typography>
                      {doc.url && (
                        <Button
                          size="small"
                          sx={{ mt: 1 }}
                          onClick={() => window.open(doc.url, '_blank')}
                        >
                          View Document
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No documents uploaded
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKycDialogOpen(false)}>Close</Button>
          {selectedMerchant && (
            <>
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  handleApproveRejectKyc(selectedMerchant, 'approve');
                  setKycDialogOpen(false);
                }}
              >
                Approve KYC
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => {
                  handleApproveRejectKyc(selectedMerchant, 'reject');
                  setKycDialogOpen(false);
                }}
              >
                Reject KYC
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Add Merchant Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, borderBottom: '1px solid #eee', mb: 1 }}>
          Onboard New Merchant
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="merchantName"
                  value={newMerchant.merchantName}
                  onChange={handleNewMerchantChange}
                  required
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Business Name"
                  name="businessName"
                  value={newMerchant.businessName}
                  onChange={handleNewMerchantChange}
                  required
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={newMerchant.email}
                  onChange={handleNewMerchantChange}
                  required
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={newMerchant.phone}
                  onChange={handleNewMerchantChange}
                  required
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Initial Balance"
                  name="initialBalance"
                  type="number"
                  value={newMerchant.initialBalance}
                  onChange={handleNewMerchantChange}
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #eee' }}>
          <Button onClick={() => setAddDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleAddMerchant} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Merchant'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lien Dialog */}
      <Dialog
        open={lienDialogOpen}
        onClose={() => setLienDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, borderBottom: '1px solid #eee', mb: 1 }}>
          {lienAction === 'add' ? 'Set Total Lien Amount' : 'Release Lien'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedMerchant && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Merchant: {selectedMerchant.merchantName || selectedMerchant.name}
              </Typography>
              {lienAction === 'add' ? (
                <>
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block">Wallet Balance</Typography>
                        <Typography variant="body2" fontWeight="medium">₹{selectedMerchant.walletBalance?.toFixed(2) || '0.00'}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block">Usable Balance</Typography>
                        <Typography variant="body2" fontWeight="medium" color="success.main">₹{selectedMerchant.usableBalance?.toFixed(2) || '0.00'}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" display="block">Current Lien Amount</Typography>
                        <Typography variant="body2" fontWeight="medium" color="error">₹{selectedMerchant.lienAmount?.toFixed(2) || '0.00'}</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                  <TextField
                    fullWidth
                    label="New Total Lien Amount"
                    type="number"
                    value={lienData.amount}
                    onChange={(e) => setLienData({ ...lienData, amount: e.target.value })}
                    sx={{ mb: 2 }}
                    required
                  />
                  <TextField
                    fullWidth
                    label="Reason"
                    value={lienData.reason}
                    onChange={(e) => setLienData({ ...lienData, reason: e.target.value })}
                    required
                  />
                </>
              ) : (
                <Typography variant="body1">
                  Are you sure you want to release the current lien of ₹{selectedMerchant.lienAmount?.toFixed(2)}?
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #eee' }}>
          <Button onClick={() => setLienDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleLienSubmit}
            variant="contained"
            color={lienAction === 'add' ? 'primary' : 'error'}
          >
            {lienAction === 'add' ? 'Update Lien' : 'Release Lien'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MerchantManagement;
